/**
 * The GOVERNMENT TAKE card (E2.1 design spec 1/4.3, demoted by round-3
 * ruling 11.4, reworked by owner ruling 2026-07-15): tax_total_minor as the
 * headline figure with an MTD delta chip beside it (same DeltaChip
 * component, coloring convention, "(vs <prev period>)" context and tooltip
 * as the statement card's chips -- tax tracks business volume, so up =
 * green, the standard direction), and tax_splits rendered AS SERVED (never
 * reconciled against the +-1 minor-unit independent-rounding drift -- E2
 * brief) as label/rate/amount rows. The proportional segmented bar + slate
 * ramp were DELETED by the same ruling. Each row's percentage is v1's
 * vocabulary (owner ruling 2026-08-12): the split's CONFIGURED share of gross
 * profit from the wire's bp, not its share of the tax total.
 * Full width on xs (E2.1 spec section 8). First load renders the
 * representative HeadlineCardSkeleton instead (owner ruling 2026-07-15).
 */
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { TaxSplit } from '../../api/index.ts';
import { MoneyText } from '../../components/MoneyText.tsx';
import { palette } from '../../theme.ts';
import { bpRateText } from './dashboardHelpers.ts';
import { DeltaChip } from './DeltaChip.tsx';
import { HeadlineCardSkeleton } from './dashboardSkeletons.tsx';
import type { ComparisonContext } from './periodState.ts';

function SplitRow({
  split,
  currencyCode,
}: Readonly<{ split: TaxSplit; currencyCode: string | null }>): React.JSX.Element {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.5 }}>
      <Typography sx={{ color: palette.textSecondary, fontSize: '0.88rem' }}>{split.label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="caption" sx={{ color: palette.textSecondary }}>
          {bpRateText(split.bp)}
        </Typography>
        <MoneyText minor={split.amount_minor} currencyCode={currencyCode} size="0.9rem" weight={600} />
      </Box>
    </Box>
  );
}

export interface GovernmentTakeCardProps {
  taxTotalMinor: number;
  /** Previous period's tax total (same MTD-capped previous summary the
   *  statement card's chips compare against) -- null/0 renders no chip. */
  previousTaxTotalMinor: number | null;
  /** The chip's current side when it differs from the headline (the
   *  day-capped MTD basis); defaults to
   *  `taxTotalMinor`. */
  chipTaxTotalMinor?: number;
  taxSplits: TaxSplit[];
  currencyCode: string | null;
  comparisonContext: ComparisonContext;
  /** FIRST load only (owner ruling 2026-07-15): representative skeleton
   *  instead of the card; later reloads keep showing the last figures. */
  loading?: boolean;
  /** False when the selected period has NO reports (owner ruling 2026-08-12):
   *  the zeros are structural, so no "-100%" chip. */
  showDeltas?: boolean;
}

export function GovernmentTakeCard({
  taxTotalMinor,
  previousTaxTotalMinor,
  chipTaxTotalMinor,
  taxSplits,
  currencyCode,
  comparisonContext,
  loading = false,
  showDeltas = true,
}: Readonly<GovernmentTakeCardProps>): React.JSX.Element {
  if (loading) return <HeadlineCardSkeleton testId="gov-take-skeleton" rows={3} padding={2.5} />;

  return (
    <Paper sx={{ p: 2.5, width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography
          variant="caption"
          sx={{ color: palette.textSecondary, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
        >
          Government take
        </Typography>
        <MoneyText minor={taxTotalMinor} currencyCode={currencyCode} size="1.25rem" weight={800} sx={{ mt: 0.25 }} />
        {showDeltas && (
          <DeltaChip
            metric="tax"
            current={chipTaxTotalMinor ?? taxTotalMinor}
            previous={previousTaxTotalMinor}
            contextLabel={comparisonContext.label}
            tooltipText={comparisonContext.tooltipText}
          />
        )}
      </Box>

      {taxSplits.length === 0 ? (
        <Typography variant="body2" sx={{ color: palette.textSecondary }}>
          No tax for this period.
        </Typography>
      ) : (
        <Box>
          {taxSplits.map((split) => (
            <SplitRow key={split.code} split={split} currencyCode={currencyCode} />
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default GovernmentTakeCard;
